import useStableId from "@ui/hooks/unique-id";

import classNames from "classnames";
import React, { ReactNode } from "react";

export interface SelectOption {
  id: string | number | undefined;
  name: string;
  description?: ReactNode;
}

export interface SelectProps<T extends SelectOption> extends React.ComponentProps<"select"> {
  options: T[];
  label?: ReactNode;
  initialValue?: T;
  onChange: (val: any) => void;
  valueKey?: keyof T;
  labelKey?: keyof T;
  labelHidden?: boolean;
  error?: string;
  disabled?: boolean;
  description?: ReactNode;
  hint?: any;
}

export interface SelectOptionProps<T> {
  option: T;
}

function NativeSelect<T extends SelectOption>(props: SelectProps<T>) {
  const id = useStableId(props);
  return (
    <div>
      {(props.label || props.error) && (
        <div
          className={classNames("mb-1 flex  items-end", {
            "justify-between": props.label && !props.labelHidden,
            "justify-end": !props.label || props.labelHidden,
          })}
        >
          {props.label && (
            <label
              htmlFor={id}
              className={classNames("block text-sm font-medium text-foreground", {
                "sr-only": props.labelHidden,
              })}
            >
              {props.label}
            </label>
          )}
          {props.error ? (
            <p className="dark:text-red-500 text-xs text-red-600">{props.error}</p>
          ) : props.hint ? (
            <span className="block text-xs text-muted-foreground">
              {typeof props.hint === "function" ? props.hint(props) : props.hint}
            </span>
          ) : null}
        </div>
      )}

      <select
        id={id}
        value={props.value}
        onChange={props.onChange}
        className="focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm block w-full py-2 pl-3 pr-10 text-base border-border rounded-md bg-background"
      >
        {props.options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export default NativeSelect;
